import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import {
  TextChannel,
  TextChannelKey,
  TurnInto,
  BrandPlan,
  BrandPlanStrategyType,
  BrandPlanStrategy as IBrandPlanStrategy,
  ContentStrategyType,
  BrandPlanTactic,
} from 'types'
import { Twitter, Facebook, LinkedIn, Instagram, YouTube, TikTok } from 'blaze-ui/icons'
import { Google } from 'almanac-ui/icons'
import Amazon from 'almanac-ui/icons/blaze/18/Amazon'
import Bing from 'almanac-ui/icons/blaze/18/Bing'
import Pinterest from 'almanac-ui/icons/blaze/18/Pinterest'
import {
  AbandonedCart,
  AboutUs,
  BattleCard,
  BillingAndPayments,
  Briefs,
  CallScript,
  CandidateNurture,
  Care,
  CaseStudies,
  Cold,
  DidntSignUp,
  Faq,
  HelpDocs,
  Impact,
  JobDescription,
  LandingPage,
  LostCustomer,
  MailNurture,
  MailPromo,
  Mission,
  OnePager,
  Outline,
  PaymentConfirmation,
  PreCallChecklist,
  PressRelease,
  Pricing,
  ReplyTemplates,
  Research,
  ReturnPolicy,
  ShippingPolicy,
  Sms,
  Testimonial,
  Warm,
  WelcomeSeries,
} from 'almanac-ui/icons/blaze/32'
import { Brainstorm, Campaign } from 'blaze-ui/icons/24'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import SquareSpace from 'almanac-ui/icons/blaze/18/SquareSpace'
import Shopify from 'almanac-ui/icons/blaze/28/Shopify'
import Etsy from 'almanac-ui/icons/blaze/28/Etsy'
import ProductDemo from 'almanac-ui/icons/blaze/32/ProductDemo'
import TurnDocsIntoContent from 'blaze-ui/icons/24/TurnDocsIntoContext'
import TurnMeetingVideosIntoContent from 'blaze-ui/icons/24/TurnMeetingsVideosIntoContent'
import TurnWebsitesIntoContent from 'blaze-ui/icons/24/TurnWebsitesIntoContent'
import TurnVoiceNotesIntoContent from 'blaze-ui/icons/24/TurnVoiceNotesIntoContent'
import { MultipleBlogPosts, MultipleInstagram } from 'almanac-ui/icons/24'
import MultipleTwitter from 'almanac-ui/icons/24/MultipleTwitter'
import MultipleFacebook from 'blaze-ui/icons/24/MultipleFacebook'
import MultipleLinkedIn from 'blaze-ui/icons/24/MultipleLinkedIn'
import Blog from 'almanac-ui/icons/blaze/24/Blog'
import { ContentPlan } from 'blaze-ui/icons/36'
import { HighDetailedMail } from 'blaze-ui/icons/40'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export const TextChannels: Record<TextChannelKey, TextChannel> = {
  [ChannelType.InstagramPosts]: {
    name: 'Instagram Text Post',
    displayName: 'instagram post',
    icon: Instagram,
    tags: ['instagram post', 'instagram', 'post', 'social', 'social media', 'social post', 'stories'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_IG_Caption_dncbs1.png',
  },
  // Used to generate regular docs with instagram captions without the social integration and publishable post stuff
  // There's no direct entry point to this channel, it's only accessible through the FlowA1 wizard
  [ChannelType.InstagramCaptions]: {
    name: 'Instagram Captions',
    displayName: 'instagram captions',
    icon: Instagram,
    tags: [],
    image: '',
  },
  [ChannelType.Tweets]: {
    name: 'X/Twitter Text Post',
    displayName: 'x/twitter post',
    icon: Twitter,
    tags: ['social media', 'social post', 'x', 'microblogging', 'threads', 'twitter', 'tweet'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_X_Text_iweyq8.png',
  },
  [ChannelType.FacebookPost]: {
    name: 'Facebook Text Post',
    displayName: 'facebook post',
    icon: Facebook,
    tags: ['facebook post', 'facebook', 'post', 'social', 'social media', 'social post'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_FB_Text_k2phml.png',
  },
  [GenerationType.InstagramPlanning]: {
    icon: MultipleInstagram,
    tags: ['instagram post', 'instagram', 'post', 'social', 'social media', 'social post', 'stories'],
    name: 'Multiple Instagram Posts',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_IG_Square_owwzlt.png',
  },
  [ChannelType.LinkedInPosts]: {
    icon: LinkedIn,
    tags: ['linkedin post', 'linkedin', 'linked', 'post', 'social', 'social media', 'social post'],
    name: 'LinkedIn Text Post',
    displayName: 'linkedIn post',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_LI_Text_wcgjar.png',
  },
  [ChannelType.TweetThread]: {
    icon: Twitter,
    tags: ['social media', 'social post', 'x', 'microblogging', 'threads', 'mastodon', 'twitter', 'tweet'],
    name: 'X/Twitter Thread',
    displayName: 'x/twitter thread',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_X_Thread_ednybx.png',
  },
  [ChannelType.InstagramReel]: {
    icon: Instagram,
    tags: [
      'instagram reel',
      'instagram',
      'ig',
      'reel',
      'post',
      'social media',
      'social post',
      'stories',
      'reels',
      'video',
      'script',
      'video script',
      'copy',
    ],
    name: 'Instagram Reel Script',
    displayName: 'instagram reel script',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_IG_Reel_Copy_xhaiwv.png',
  },
  [ChannelType.YoutubeScript]: {
    icon: YouTube,
    tags: [
      'youtube script',
      'youtube',
      'script',
      'video script',
      'social media',
      'social post',
      'video',
      'vimeo',
      'clip',
    ],
    name: 'YouTube Script',
    displayName: 'youtube script',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_YT_Script_hjbs9y.png',
  },
  [ChannelType.TikTokScript]: {
    icon: TikTok,
    tags: [
      'tiktok script',
      'tiktok',
      'script',
      'video script',
      'social media',
      'social post',
      'video',
      'reels',
      'instagram reel',
      'clip',
    ],
    name: 'TikTok Script',
    displayName: 'tiktok script',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Tiktok_Script_bokte6.png',
  },
  [ChannelType.AmazonAds]: {
    icon: Amazon,
    tags: ['amazon ads', 'amazon', 'ad', 'advertisement', 'display', 'ads', 'sponsored brand'],
    name: 'Amazon Ad Copy',
    displayName: 'amazon ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Amazon_ezjb32.png',
  },
  [ChannelType.BingAds]: {
    icon: Bing,
    tags: ['bing ads', 'bing', 'ad', 'advertisement', 'ads', 'search ads'],
    name: 'Bing Ad Copy',
    displayName: 'bing ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Bing_Ad_pngwyv.png',
  },
  [ChannelType.GoogleAds]: {
    icon: Google,
    tags: ['google ads', 'google', 'ad', 'advertisement', 'ads', 'search ads'],
    name: 'Google Ad Copy',
    displayName: 'google ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Google_Ad_s6puvr.png',
  },
  [DesignChannel.GoogleMyBusinessPost]: {
    icon: Google,
    tags: ['google my business', 'google', 'gmb', 'post', 'business profile', 'google business'],
    name: 'Google Business Profile Post',
    displayName: 'google business profile post',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Google_Ad_s6puvr.png',
  },
  [ChannelType.FacebookAds]: {
    icon: Facebook,
    tags: ['facebook ads', 'facebook', 'ad', 'advertisement', 'ads', 'social ads'],
    name: 'Facebook Ad Copy',
    displayName: 'facebook ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Facebook_Copy_y2jin9.png',
  },
  [ChannelType.LinkedInAds]: {
    icon: LinkedIn,
    tags: ['linkedin ads', 'linkedin', 'ad', 'advertisement', 'ads', 'social ads'],
    name: 'LinkedIn Ad Copy',
    displayName: 'linkedIn ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_LinkedIn_Ad_pxcadq.png',
  },
  [ChannelType.PinterestAds]: {
    icon: Pinterest,
    tags: ['pinterest ads', 'pinterest', 'ad', 'advertisement', 'ads', 'social ads'],
    name: 'Pinterest Ad Copy',
    displayName: 'pinterest ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Pinterest_mlp0p3.png',
  },
  [ChannelType.TikTokAds]: {
    icon: TikTok,
    tags: ['tiktok ads', 'tiktok', 'ad', 'advertisement', 'ads', 'video ads', 'social ads'],
    name: 'TikTok Ad Copy',
    displayName: 'tiktok ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Tiktok_Ad_vegoc5.png',
  },
  [ChannelType.YoutubeAds]: {
    icon: YouTube,
    tags: ['youtube ads', 'youtube', 'ad', 'advertisement', 'ads', 'video ads'],
    name: 'YouTube Ad Copy',
    displayName: 'youtube ad copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_YT_Ad_cmoyjv.png',
  },
  [ChannelType.BlogPost]: {
    icon: Blog,

    tags: ['blog post', 'blog', 'website', 'article', 'post', 'seo', 'digital marketing', 'long form', 'content'],
    name: 'Blog Post',
    displayName: 'blog post',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Blog_karghq.png',
  },
  [ChannelType.BlogIdeas]: {
    icon: Blog,
    tags: ['blog ideas', 'blog', 'blog topics', 'article', 'long form', 'content planning'],
    name: 'Blog Ideas',
    displayName: 'blog ideas document',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Blog_Post_teqi2h.png',
  },
  [ChannelType.CaseStudy]: {
    icon: CaseStudies,
    tags: [
      'case study',
      'case',
      'study',
      'article',
      'long form',
      'customer profile',
      'client testimonial',
      'testimonial',
    ],
    name: 'Case Study',
    displayName: 'case study',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Case_Study_iicpju.png',
  },
  [ChannelType.PressRelease]: {
    icon: PressRelease,
    tags: ['press release', 'pr', 'press', 'release', 'article', 'news brief', 'announcement'],
    name: 'Press Release',
    displayName: 'press release',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Press_Release_xphhfp.png',
  },
  [GenerationType.Outline]: {
    name: 'Content Outline',
    displayName: 'content outline',
    icon: Outline,
    tags: ['content outline', 'content', 'outline', 'article'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Content_Outline_tafzrz.png',
  },
  [GenerationType.ContentBrief]: {
    name: 'Content Brief',
    displayName: 'content brief',
    icon: Briefs,
    tags: ['content brief', 'content', 'brief', 'outline', 'article'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Content_Brief_fdygyu.png',
  },
  [ChannelType.PromotionalEmailSequence]: {
    icon: MailPromo,

    tags: ['promotional email', 'promotional', 'email', 'promote', 'discount', 'offers'],
    name: 'Promotional Email Sequence',
    displayName: 'promotional email sequence',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-1_zqmtrc.png',
  },
  [ChannelType.ColdOutboundEmailSequence]: {
    icon: Cold,
    tags: ['cold outbound', 'cold', 'outbound', 'email', 'email reach out', 'sales', 'lead generation', 'outreach'],
    name: 'Cold Outbound Email Sequence',
    displayName: 'cold outbound email sequence',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email_cold_tuhi5y.png',
  },
  [ChannelType.WarmOutboundEmailSequence]: {
    icon: Warm,
    tags: ['warm outbound', 'warm', 'outbound', 'email', 'email reach out', 'sales', 'outreach'],
    name: 'Warm Outbound Email Sequence',
    displayName: 'warm outbound email sequence',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-9_jjwyin.png',
  },
  [ChannelType.SMSMessages]: {
    icon: Sms,
    tags: ['sms messages', 'sms', 'message', 'text', 'texts', 'whatsapp'],
    name: 'SMS Message Copy',
    displayName: 'SMS message copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-8_iiagmq.png',
  },
  [ChannelType.Newsletter]: {
    icon: HighDetailedMail,
    tags: ['newsletter', 'roundup', 'email', 'organic', 'copy'],
    name: 'Email Newsletter',
    displayName: 'Email Newsletter',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email_nig1pc.png',
  },
  [ChannelType.NewsletterIdeas]: {
    icon: Brainstorm,

    tags: ['newsletter ideas', 'newsletter', 'newsletter topics', 'email', 'organic'],
    name: 'Newsletter Ideas',
    displayName: 'newsletter ideas',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Ideas_lrtdp5.png',
  },
  [ChannelType.NurtureEmailSequence]: {
    icon: MailNurture,
    tags: ['nurture sequence', 'nurture', 'sequence', 'email', 'product emails', 'lifecycle emails', 'onboarding'],
    name: 'Nurture Sequence',
    displayName: 'nurture sequence',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-2_o2wb9t.png',
  },
  [ChannelType.WelcomeSeries]: {
    icon: WelcomeSeries,
    tags: [
      'welcome series',
      'welcome',
      'series',
      'email',
      'product emails',
      'lifecycle',
      'lifecycle emails',
      'onboarding',
    ],
    name: 'Welcome Series',
    displayName: 'welcome series copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-3_y9yuzv.png',
  },
  [ChannelType.DidntSignUp]: {
    icon: DidntSignUp,
    tags: [
      'sign up',
      'email',
      'abandon',
      'lifecycle',
      'product emails',
      'lifecycle emails',
      'lead recovery',
      're-engagement',
    ],
    name: "Didn't Sign Up",
    displayName: "didn't sign up copy",
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-5_r9wdow.png',
  },
  [ChannelType.AbandonedCart]: {
    icon: AbandonedCart,
    tags: [
      'abandoned cart',
      'email',
      'abandoned',
      'cart',
      'product emails',
      'lifecycle',
      'lifecycle emails',
      'checkout reminders',
      'checkout',
      're-engagement',
    ],
    name: 'Abandoned Cart',
    displayName: 'abandoned cart copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-6_tunuhi.png',
  },
  [ChannelType.LostCustomer]: {
    icon: LostCustomer,
    tags: [
      'lost customer',
      'lost',
      'customer',
      'email',
      'lifecycle',
      'product emails',
      'lifecycle emails',
      're-engagement',
    ],
    name: 'Lost Customer',
    displayName: 'lost customer copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Email-7_ifwaax.png',
  },
  [ChannelType.ProductLaunchWebsite]: {
    icon: SquareSpace,
    tags: [
      'on your website',
      'on',
      'your',
      'website',
      'product description',
      'product info',
      'product listing',
      'ecommerce',
      'copy',
      'product launch',
    ],
    name: 'Website Product Copy',
    displayName: 'website product copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Website_Copy_gvrazl.png',
  },
  [ChannelType.ProductLaunchAmazon]: {
    icon: Amazon,
    tags: [
      'amazon',
      'website',
      'product description',
      'product info',
      'product listing',
      'ecommerce',
      'product',
      'product name',
      'copy',
    ],
    name: 'Amazon Product Copy',
    displayName: 'amazon product copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Amazon_Copy_h1shmc.png',
  },
  [ChannelType.ProductLaunchShopify]: {
    icon: Shopify,
    tags: [
      'shopify',
      'website',
      'product description',
      'product info',
      'product listing',
      'ecommerce',
      'product',
      'product name',
      'copy',
    ],
    name: 'Shopify Product Copy',
    displayName: 'shopify product copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Shopify_Copy_mabsym.png',
  },
  [ChannelType.ProductLaunchEtsy]: {
    icon: Etsy,
    tags: [
      'etsy',
      'website',
      'product description',
      'product info',
      'product listing',
      'ecommerce',
      'product',
      'product name',
      'copy',
    ],
    name: 'Etsy Product Copy',
    displayName: 'etsy product copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Etsy_Copy_a2qt5a.png',
  },
  [ChannelType.ProductLaunchGoogle]: {
    icon: Google,
    tags: [
      'google',
      'website',
      'product description',
      'product info',
      'product listing',
      'ecommerce',
      'product',
      'product name',
      'shop',
      'copy',
    ],
    name: 'Google Shop Product Copy',
    displayName: 'google shop product copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Google_Copy_qo7ubd.png',
  },
  [ChannelType.OnePager]: {
    icon: OnePager,
    tags: ['one pager', 'one', 'pager', 'sales', 'research', 'sales collateral', 'sales enablement'],
    name: 'Sales One Pager',
    displayName: 'sales one pager copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_One_Pager_gwbs2p.png',
  },
  [ChannelType.BattleCard]: {
    icon: BattleCard,
    tags: ['battler card', 'battle', 'ard', 'sales', 'research', 'competitors', 'sales collateral', 'sales enablement'],
    name: 'Sales Battle Card',
    displayName: 'sales battle card copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Battle_card_hpt2rd.png',
  },
  [ChannelType.Testimonial]: {
    icon: Testimonial,
    tags: ['testimonial', 'page', 'sales', 'customer', 'social proof'],
    name: 'Sales Testimonial',
    displayName: 'sales testimonial copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Type7_o1y30x.png',
  },
  [ChannelType.ProductDemo]: {
    icon: ProductDemo,
    tags: ['product demo', 'product', 'demo', 'sales', 'demos', 'pitch', 'sales collateral', 'sales enablement'],
    name: 'Product Demo Script',
    displayName: 'product demo script',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Product_Demo_qccfwt.png',
  },
  [ChannelType.PreCallChecklist]: {
    icon: PreCallChecklist,
    tags: ['pre-call checklist sales', 'research', 'precall', 'pre call'],
    name: 'Pre-call Checklist',
    displayName: 'pre-call checklist',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Pre-call_qtix8p.png',
  },
  [ChannelType.CallScript]: {
    icon: CallScript,
    tags: ['call script', 'script', 'sales', 'demos', 'pitch'],
    name: 'Sales Call Script',
    displayName: 'sales call script',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Sales_Call_zjsdlz.png',
  },
  [ChannelType.Research]: {
    icon: Research,
    tags: ['research', 'sales'],
    name: 'Customer Research Brief',
    displayName: 'customer research brief',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Research_xjlguu.png',
  },
  [ChannelType.ReplyTemplates]: {
    name: 'Customer Reply Templates',
    displayName: 'customer reply templates',
    icon: ReplyTemplates,
    tags: ['reply', 'customer support', 'customer service', 'customer success'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Reply_Templates_r2hh2g.png',
  },
  [ChannelType.HelpDocs]: {
    name: 'Customer Help Docs',
    displayName: 'customer help docs copy',
    icon: HelpDocs,
    tags: [
      'help docs',
      'help',
      'customer support',
      'customer service',
      'user guide',
      'customer success',
      'documentation',
      'faq',
      'knowledge base',
    ],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Help_Docs_khuwx5.png',
  },
  [ChannelType.ReturnPolicy]: {
    name: 'Return Policy',
    displayName: 'return policy copy',
    icon: ReturnPolicy,
    tags: ['return policy', 'return', 'policy', 'customer support', 'customer service', 'service policy'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Type7-1_y0r6n2.png',
  },
  [ChannelType.ShippingPolicy]: {
    name: 'Shipping Policy',
    displayName: 'shipping policy',
    icon: ShippingPolicy,
    tags: ['shipping policy', 'shipping', 'policy', 'customer support', 'customer service', 'delivery information'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Shipping_tpbiaf.png',
  },
  [ChannelType.CareInstructions]: {
    name: 'Product Care Instructions',
    displayName: 'product care instructions copy',
    icon: Care,
    tags: ['care instructions', 'care', 'instructions', 'customer support', 'customer service'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Care_Instructions_nzdghh.png',
  },
  [ChannelType.BillingAndPayments]: {
    name: 'Billing and Payments FAQs',
    displayName: 'billing and payments FAQs copy',
    icon: BillingAndPayments,
    tags: ['billing and payments', 'billing', 'payments', 'customer support', 'customer service', 'invoicing'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Billing_eiuwj9.png',
  },
  [ChannelType.PaymentConfirmation]: {
    name: 'Payment Confirmation Copy',
    displayName: 'payment confirmation copy',
    icon: PaymentConfirmation,
    tags: ['payment confirmation', 'customer support', 'customer service', 'service', 'emails'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Type4_z841kr.png',
  },
  [ChannelType.Mission]: {
    icon: Mission,
    tags: ['mission', 'website', 'site', 'page', 'company', 'vision', 'purpose'],
    name: 'Mission Page',
    displayName: 'mission page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Mission_bt4bvk.png',
  },
  [ChannelType.Impact]: {
    icon: Impact,
    tags: ['impact', 'website', 'site', 'page', 'company', 'vision', 'purpose'],
    name: 'Impact Page',
    displayName: 'impact page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Impact_paflxs.png',
  },
  [ChannelType.JobDescription]: {
    icon: JobDescription,
    tags: [
      'job description',
      'job',
      'description',
      'site',
      'page',
      'company',
      'website',
      'recruiting',
      'recruitment',
      'careers',
      'position',
      'hiring',
    ],
    name: 'Job Description Copy',
    displayName: 'job description copy',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Job_bnisja.png',
  },
  [ChannelType.FAQs]: {
    icon: Faq,
    tags: ['faq', 'frequently', 'asked', 'website', 'site', 'page', 'support', 'help', 'questions', 'service'],
    name: 'FAQ Page',
    displayName: 'FAQ page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_FAQ_bspn7b.png',
  },
  [ChannelType.LandingPage]: {
    icon: LandingPage,
    tags: ['landing page', 'page', 'website', 'site', 'homepage', 'page', 'marketing', 'lead generation'],
    name: 'Landing Page',
    displayName: 'landing page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Landing_ts94ae.png',
  },
  [ChannelType.AboutUs]: {
    icon: AboutUs,
    tags: ['about us', 'about', 'us', 'website', 'site', 'page', 'company', 'team'],
    name: 'About Us Page',
    displayName: 'about us page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_About_Us_cpk4be.png',
  },
  [ChannelType.Pricing]: {
    icon: Pricing,
    tags: ['pricing', 'price', 'website', 'site', 'subscriptions', 'cost'],
    name: 'Pricing Page',
    displayName: 'pricing page',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Pricing_rsrjx6.png',
  },
  [ChannelType.CandidateNurtureSequence]: {
    icon: CandidateNurture,
    tags: [
      'candidate nurture',
      'candidate',
      'nurture',
      'site',
      'page',
      'website',
      'recruiting',
      'recruitment',
      'hiring',
    ],
    name: 'Candidate Nurture Email Sequence',
    displayName: 'candidate nurture email sequence',
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Type_Welcome_Sequence_r7su59.png',
  },
  [GenerationType.Campaign]: {
    name: 'Multi Channel Campaign',
    icon: Campaign,
    tags: ['campaign', 'channel', 'multi', 'multi channel'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Campaign_fbyxlt.png',
  },
  [GenerationType.Brainstorm]: {
    name: 'Brainstorm Ideas',
    displayName: 'brainstorm ideas document',
    icon: Brainstorm,
    tags: ['brain', 'brainstorm', 'storm', 'ideas'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Brainstorm_bx4plb.png',
  },
  [TurnInto.DocsIntoContent]: {
    name: 'Turn Docs into content',
    icon: TurnDocsIntoContent,
    tags: [],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Doc_bdrlm4.png',
  },
  [TurnInto.VideoIntoContent]: {
    name: 'Turn Video Files into Content',
    icon: TurnMeetingVideosIntoContent,
    tags: [],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Video_kk7jhc.png',
  },
  [TurnInto.WebsitesIntoContent]: {
    name: 'Turn Websites into Content',
    icon: TurnWebsitesIntoContent,
    tags: [],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Website_jnlraw.png',
  },
  [TurnInto.AudioIntoContent]: {
    name: 'Turn Audio files into Content',
    icon: TurnVoiceNotesIntoContent,
    tags: [],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Audio_ydtnbq.png',
  },
  [GenerationType.ContentPlan]: {
    name: 'Content Plan',
    icon: ContentPlan,
    tags: ['content', 'plan', 'planning', 'strategy'],
    image: 'https://res.cloudinary.com/almanac/image/upload/v1737151943/blaze_assets/create_new_v3/Ideas_zzkqll.png',
  },
  [GenerationType.SEOPlanning]: {
    name: 'Multiple Blog Posts',
    icon: MultipleBlogPosts,
    tags: ['SEO', 'blogs', 'blog', 'planning', 'search engine optimization', 'multiple posts'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Blog_cfoyse.png',
  },
  [GenerationType.TwitterPlanning]: {
    name: 'Multiple X/Twitter Posts',
    icon: MultipleTwitter,
    tags: ['social media', 'social post', 'x', 'microblogging', 'threads', 'twitter', 'tweet', 'multiple posts'],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_X_zoqoh7.png',
  },
  [GenerationType.FacebookPlanning]: {
    name: 'Multiple Facebook Posts',
    icon: MultipleFacebook,
    tags: [
      'facebook post',
      'facebook',
      'post',
      'social',
      'social media',
      'social post',
      'multiple',
      'multiple facebook',
      'multiple facebook posts',
    ],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Facebook_xpeekj.png',
  },
  [GenerationType.LinkedInPlanning]: {
    name: 'Multiple LinkedIn Posts',
    icon: MultipleLinkedIn,
    tags: [
      'linkedin post',
      'linkedin',
      'linked',
      'post',
      'social',
      'social media',
      'social post',
      'multiple posts',
      'multiple linkedin posts',
    ],
    image:
      'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/create_new_v3/Property_1_Variant3_d1sxba.png',
  },
}

export const BrandPlanIntensity: Record<BrandPlan['intensity'], string> = {
  10: 'Steady Growth',
  20: 'Balanced Expansion',
  30: 'Steady Growth',
  40: 'Disruptive Acceleration',
}

export const BrandPlanStrategy: Record<BrandPlanStrategyType, IBrandPlanStrategy> = {
  [BrandPlanStrategyType.Evergreen]: {
    name: 'Evergreen',
    description: 'Channel specific topics',
  },
  [BrandPlanStrategyType.Crosspost]: {
    name: 'Crosspost',
    description: 'Theme',
  },
}

export const ContentStrategyTypeLabel: Record<ContentStrategyType, string> = {
  [ContentStrategyType.EducationalContent]: 'Educational Content',
  [ContentStrategyType.QuickTips]: 'Quick Tips',
  [ContentStrategyType.ThoughtLeadership]: 'Thought Leadership',
  [ContentStrategyType.LifestyleContent]: 'Lifestyle Content',
  [ContentStrategyType.ProblemSolution]: 'Problem/Solution',
  [ContentStrategyType.ProductEducation]: 'Product Education',
  [ContentStrategyType.OfferAndServices]: 'Offer & Services',
  [ContentStrategyType.OfferAndPromotion]: 'Offer & Promotion',
}

export const TACTIC_PROVIDERS: Record<BrandPlanTactic, ExternalIntegrationEnum[]> = {
  [BrandPlanTactic.Instagram]: [ExternalIntegrationEnum.Instagram],
  [BrandPlanTactic.InstagramReel]: [ExternalIntegrationEnum.Instagram],
  [BrandPlanTactic.Facebook]: [ExternalIntegrationEnum.Facebook],
  [BrandPlanTactic.LinkedIn]: [ExternalIntegrationEnum.LinkedIn],
  [BrandPlanTactic.Twitter]: [ExternalIntegrationEnum.X],
  [BrandPlanTactic.TikTok]: [ExternalIntegrationEnum.TikTok],
  [BrandPlanTactic.YouTube]: [ExternalIntegrationEnum.YouTube],
  [BrandPlanTactic.MetaAds]: [ExternalIntegrationEnum.MetaAds],
  [BrandPlanTactic.GoogleBusinessProfile]: [ExternalIntegrationEnum.GoogleBusinessProfile],
  [BrandPlanTactic.SEO]: [
    ExternalIntegrationEnum.WordPress,
    ExternalIntegrationEnum.GoHighLevel,
    ExternalIntegrationEnum.Wix,
    ExternalIntegrationEnum.Shopify,
  ],
  [BrandPlanTactic.Email]: [ExternalIntegrationEnum.Mailchimp],
}
